import React from 'react'

export function getProgressCourse(course) {
  if (!course) return 0

  const totalStages = course.stages.length
  if (totalStages === 0) return 0

  let totalStageProgress = 0

  course.stages.forEach((stage) => {
    if (stage.stage_badges.length > 0) {
      // Calculate the total progress of all badges in this stage
      const totalBadgeProgress = stage.stage_badges.reduce(
        (accumulatedProgress, stage_badge) => {
          const badgeProgress =
            stage_badge.user_badge_progress.length > 0
              ? stage_badge.user_badge_progress[0].completion_percentage
              : 0
          return accumulatedProgress + badgeProgress
        },
        0
      )

      // Get the average progress for the stage
      const stageProgress = totalBadgeProgress / stage.stage_badges.length

      // Add this stage's progress to the overall total progress for the course
      totalStageProgress += stageProgress
    }
  })

  // Calculate the course progress as the average of all stage progress
  const courseProgress = totalStageProgress / totalStages

  return parseInt(courseProgress)
}

export function getProgressStage(stage) {
  if (!stage.stage_badges || stage.stage_badges.length === 0) return 0

  // Sum up the progress for all badges in the stage
  const totalProgress = stage.stage_badges.reduce(
    (accumulatedProgress, stage_badge) => {
      const badgeProgress =
        stage_badge.user_badge_progress.length > 0
          ? stage_badge.user_badge_progress[0].completion_percentage
          : 0
      return accumulatedProgress + badgeProgress
    },
    0
  )

  // Calculate the average progress for the stage
  const averageProgress = totalProgress / stage.stage_badges.length

  return parseInt(averageProgress)
}

export function getCourseStatus(course) {
  if (!course) return 'un_start'
  if (!course.stages || course.stages.length === 0) return 'un_start'

  let allStagesCompleted = true
  let hasProgress = false

  for (const stage of course.stages) {
    if (!stage.stage_badges || stage.stage_badges.length === 0) {
      allStagesCompleted = false
      continue
    }

    // Check progress for all badges in this stage
    for (const stage_badge of stage.stage_badges) {
      if (
        !stage_badge.user_badge_progress ||
        stage_badge.user_badge_progress.length === 0
      ) {
        allStagesCompleted = false // No progress on this badge
      } else {
        const progress =
          stage_badge.user_badge_progress[0].completion_percentage
        hasProgress = true // At least one badge has progress
        if (progress < 100) {
          allStagesCompleted = false // This badge is not fully completed
        }
      }
    }
  }

  if (!hasProgress) return 'un_start' // No progress on any badge
  if (allStagesCompleted) return 'completed' // All badges across all stages are completed
  return 'in_progress' // At least some badges are in progress, but not all are complete
}

export function getStageStatus(stage) {
  if (!stage) return 'un_start'
  if (!stage.stage_badges || stage.stage_badges.length === 0) return 'un_start'

  let allBadgesCompleted = true
  let hasProgress = false

  // Iterate through all badges in the stage
  for (const stage_badge of stage.stage_badges) {
    if (
      !stage_badge.user_badge_progress ||
      stage_badge.user_badge_progress.length === 0
    ) {
      allBadgesCompleted = false // No progress on this badge
    } else {
      const progress = stage_badge.user_badge_progress[0].completion_percentage
      hasProgress = true // At least one badge has progress
      if (progress < 100) {
        allBadgesCompleted = false // This badge is not fully completed
      }
    }
  }

  if (!hasProgress) return 'un_start' // No progress on any badge
  if (allBadgesCompleted) return 'completed' // All badges in the stage are completed
  return 'in_progress' // Some badges are in progress but not all are complete
}

export function convertTimeToText(timeString) {
  const [hours, minutes, seconds] = timeString.split(':').map(Number)

  let timeText = ''

  if (hours > 0) {
    timeText += `${hours} hour${hours > 1 ? 's' : ''}`
  }

  if (minutes > 0) {
    if (timeText) timeText += ' '
    timeText += `${minutes} minute${minutes > 1 ? 's' : ''}`
  }

  if (seconds > 0) {
    if (timeText) timeText += ' '
    timeText += `${seconds} second${seconds > 1 ? 's' : ''}`
  }

  if (!timeText) {
    timeText = '0 minutes'
  }

  return timeText
}

export function getCompletedBadgesCount(course) {
  if (!course) return 0
  const totalStages = course.stages.length
  if (totalStages === 0) return 0

  let completedStages = 0

  course.stages.forEach((stage) => {
    if (stage.stage_badges && stage.stage_badges.length > 0) {
      stage.stage_badges.forEach((stage_badge) => {
        const hasEarnedBadge =
          stage_badge.user_badge_progress.length > 0
            ? stage_badge.user_badge_progress[0].earned_at !== null
            : false

        if (hasEarnedBadge) {
          completedStages += 1
        }
      })
    }
  })

  course.course_badges.forEach((course_badge) => {
    const hasEarnedBadge =
      course_badge.user_badge_progress.length > 0
        ? course_badge.user_badge_progress[0].earned_at !== null
        : false

    if (hasEarnedBadge) {
      completedStages += 1
    }
  })

  return completedStages
}

export function getBadgesCount(course) {
  if (!course) return 0
  const totalStages = course.stages.length
  if (totalStages === 0) return 0

  let completedStages = 0

  course.stages.forEach((stage) => {
    if (stage.stage_badges && stage.stage_badges.length > 0) {
      stage.stage_badges.forEach((stage_badge) => {
        completedStages += 1
      })
    }
  })

  course.course_badges.forEach((course_badge) => {
    completedStages += 1
  })

  return completedStages
}

export function isEarnedStage(stage) {
  try {
    // Check if all badges have been earned
    return stage.stage_badges.every(
      (stage_badge) =>
        stage_badge.user_badge_progress.length > 0 &&
        stage_badge.user_badge_progress[0].earned_at !== null
    )
  } catch (e) {
    return false // Return false if an error occurs or badges are missing
  }
}

export function isEarnedCourse(course) {
  try {
    // Check if all badges have been earned
    return course.course_badges.every(
      (course_badge) =>
        course_badge.user_badge_progress.length > 0 &&
        course_badge.user_badge_progress[0].earned_at !== null
    )
  } catch (e) {
    return false // Return false if an error occurs or badges are missing
  }
}

export function getFirstUnearnedBadge(stage) {
  if (!stage || !stage.stage_badges || stage.stage_badges.length === 0) {
    return null // Return null if the stage or badges are not defined
  }

  // Find the first badge that hasn't been earned
  const unearnedBadge = stage.stage_badges.find(
    (stage_badge) =>
      stage_badge.user_badge_progress.length > 0 &&
      stage_badge.user_badge_progress[0].completion_percentage < 100
  )

  return unearnedBadge || null // Return the badge if found, otherwise return null
}

export const renderStatus = (status) => {
  switch (status) {
    case 'not_started':
    default:
      return (
        <p className="mb-2 text-sm text-[#CE3C56] py-1 px-2 rounded-lg bg-[#FAE9EC] w-fit">
          Not start
        </p>
      )
    case 'in_progress':
      return (
        <p className="mb-2 text-sm text-[#FF7D1F] py-1 px-2 rounded-lg bg-[#FEF5E5] w-fit">
          In progress
        </p>
      )

    case 'completed':
      return (
        <p className="mb-2 text-sm text-[#0C9247] py-1 px-2 rounded-lg bg-[#E1F1E8] w-fit">
          Completed
        </p>
      )
  }
}

export function getIdFromUrl(url) {
  if (!url) return null
  const regex = /\/([a-zA-Z0-9]+)\.glb$/
  const match = url.match(regex)

  if (match && match[1]) {
    return match[1]
  }

  return null
}

export function checkStatusMainCourses(mainCourses) {
  return mainCourses.every((course) => {
    return course.course_badges.every((courseBadge) =>
      courseBadge.user_badge_progress.every(
        (progress) => progress.completion_percentage === 100
      )
    )
  })
}

export async function generatePassword(key) {
  const encoder = new TextEncoder()
  const data = encoder.encode(key)

  const hashBuffer = await crypto.subtle.digest('SHA-256', data)

  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')

  return hashHex.slice(0, 16)
}
